import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectUser,
  logout,
  selectInitializedUser,
  fetchAllFuelTanks,
  selectAllFuelTanks,
} from '../../redux';

import {
  Box,
  Container,
  Flex,
  Portal,
  CloseButton,
  HStack,
  Text,
  Image,
  Button,
  VStack,
  Icon,
  Drawer,
  useBreakpointValue,
  createListCollection,
} from '@chakra-ui/react';

import { FaBars } from 'react-icons/fa';
import { LuArrowRightToLine } from 'react-icons/lu';

import { NavLink, FuelTankSelect } from '..';

import { getDynamicPath, getVisibleNavLinks } from '../../utils';

import { CONSTANTS } from './CONSTANTS';

export const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const allTanks = useSelector(selectAllFuelTanks);

  const user = useSelector(selectUser);
  const role = user?.role;
  const initializedUser = useSelector(selectInitializedUser);

  const [value, setValue] = useState(['Бочка']);

  const dynamicPath = useMemo(
    () => (initializedUser ? getDynamicPath(role) : '/'),
    [initializedUser, role]
  );

  const navLinks = useMemo(
    () => (initializedUser ? getVisibleNavLinks(role, CONSTANTS.NAVLINKS) : []),
    [initializedUser, role]
  );

  const allMainTanks = useMemo(
    () => allTanks.filter(({ is_main }) => is_main),
    [allTanks]
  );

  const tanksCollection = createListCollection({
    items: (Array.isArray(allMainTanks) ? allMainTanks : []).map(
      ({ id, name }) => ({
        value: String(id),
        label: name,
      })
    ),
  });

  const showHamburger = useBreakpointValue(
    navLinks.length > 4 ? { base: true, xl: false } : { base: true, lg: false }
  );

  useEffect(() => {
    dispatch(fetchAllFuelTanks());
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/fuel-tank') {
      navigate('/fuel-tank/1', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleChange = (selectedOption) => {
    const selectedId = Array.isArray(selectedOption.value)
      ? selectedOption.value[0]
      : selectedOption.value;

    setValue([selectedId]);
    navigate(`/fuel-tank/${selectedId}`);
  };

  const renderNavWithTankSelect = (isMobile = false) => (
    <>
      {role === 'accountant' && allMainTanks.length > 1 && (
        <FuelTankSelect
          tanksCollection={tanksCollection}
          value={value}
          handleChange={handleChange}
          hidePortal={isMobile}
          maxW={isMobile ? undefined : '180px'}
          minW={isMobile ? undefined : '120px'}
          size={isMobile ? 'sm' : 'md'}
        />
      )}

      {navLinks.map(({ path, name }, _) => {
        if (name === 'Заправка') {
          return (
            <React.Fragment key={name}>
              <NavLink
                to={path}
                fontSize={isMobile ? 'lg' : undefined}
                p={isMobile ? 2 : undefined}
                w={isMobile ? '100%' : undefined}>
                {name}
              </NavLink>

              {role !== 'accountant' && allMainTanks.length > 1 && (
                <FuelTankSelect
                  tanksCollection={tanksCollection}
                  value={value}
                  handleChange={handleChange}
                  hidePortal={isMobile}
                  maxW={isMobile ? undefined : '180px'}
                  minW={isMobile ? undefined : '120px'}
                  size={isMobile ? 'sm' : 'xs'}
                />
              )}
            </React.Fragment>
          );
        }

        if (name === 'Бочка') {
          if (allMainTanks.length <= 1) {
            return (
              <NavLink
                to={path}
                key={name}
                fontSize={isMobile ? 'lg' : undefined}
                p={isMobile ? 2 : undefined}
                w={isMobile ? '100%' : undefined}>
                {name}
              </NavLink>
            );
          }
          return null;
        }

        return (
          <NavLink
            to={path}
            key={name}
            fontSize={isMobile ? 'lg' : undefined}
            p={isMobile ? 2 : undefined}
            w={isMobile ? '100%' : undefined}>
            {name}
          </NavLink>
        );
      })}
    </>
  );

  return (
    <Box bg='gray.800' w='full' position='sticky' top={0} zIndex='100'>
      <Container
        px={{ base: 4, md: 8, lg: 10, xl: 12 }}
        py={4}
        maxW='container.xl'>
        <Flex
          h='full'
          alignItems='center'
          justifyContent='space-between'
          gap='2'>
          <Flex
            gap='2'
            as='button'
            alignItems='center'
            cursor='pointer'
            onClick={() => navigate(dynamicPath)}
            transition='opacity 0.8s'
            _hover={{ opacity: 0.7 }}>
            <Image src='/icon.png' boxSize='10' alt='Логотип природного газу' />
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              fontWeight='bold'
              color='white'
              textTransform='uppercase'>
              {CONSTANTS.FuelAccounting}
            </Text>
          </Flex>

          <Flex flex='1' justify='center'>
            {!showHamburger && (
              <HStack as='nav' gap='3' whiteSpace='nowrap'>
                {renderNavWithTankSelect(false)}
              </HStack>
            )}
          </Flex>

          <Flex alignItems='center' gap={2}>
            {showHamburger && (
              <Drawer.Root
                placement='start'
                size='full'
                justifyContent='center'
                alignItems='center'>
                <Drawer.Trigger asChild>
                  <Button
                    variant='ghost'
                    color='white'
                    fontSize='2xl'
                    borderColor='gray.400'
                    rounded='lg'
                    _hover={{ bg: 'gray.100', color: 'gray.900' }}>
                    <Icon as={FaBars} boxSize={6} />
                  </Button>
                </Drawer.Trigger>
                <Portal>
                  <Drawer.Backdrop />
                  <Drawer.Positioner>
                    <Drawer.Content
                      bg='gray.800'
                      color='white'
                      minW='60vw'
                      maxW='80vw'>
                      <Flex justify='flex-end' p={4}>
                        <Drawer.CloseTrigger asChild>
                          <CloseButton
                            size='lg'
                            color='white'
                            _hover={{
                              bg: 'gray.100',
                              color: 'gray.900',
                            }}
                          />
                        </Drawer.CloseTrigger>
                      </Flex>
                      <Drawer.Body>
                        <VStack mt={8} gap={4}>
                          {renderNavWithTankSelect(true)}
                        </VStack>
                      </Drawer.Body>
                    </Drawer.Content>
                  </Drawer.Positioner>
                </Portal>
              </Drawer.Root>
            )}

            <Button
              variant='ghost'
              rounded='lg'
              color='white'
              fontWeight='bold'
              borderColor='gray.400'
              _hover={{ bg: 'gray.100', color: 'gray.900' }}
              onClick={handleLogout}
              alignSelf='flex-end'>
              {CONSTANTS.Exit}
              <Icon as={LuArrowRightToLine} />
            </Button>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
};
